import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Search,
  Eye,
  EyeOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { authHeaders, formatApiErrorDetail } from "@/lib/auth";

const DEFAULT_CATEGORIES = [
  "Account & Getting Started",
  "Medications & Reminders",
  "Health Tracking & Reports",
  "Emergency & SOS Features",
  "Digital Health Records",
  "Services & Shopping",
  "Caregiver Support",
];

const EMPTY_FORM = {
  question: "",
  answer: "",
  category: DEFAULT_CATEGORIES[0],
  sort_order: 0,
  is_published: true,
};

export default function FAQPanel() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/faqs", { headers: authHeaders() });
      setFaqs(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message || "Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-clear success message
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? faqs.filter(
          (f) =>
            f.question.toLowerCase().includes(q) ||
            f.answer.toLowerCase().includes(q) ||
            f.category.toLowerCase().includes(q),
        )
      : faqs;
    const map = new Map();
    for (const f of filtered) {
      const arr = map.get(f.category) || [];
      arr.push(f);
      map.set(f.category, arr);
    }
    for (const [k, arr] of map) {
      arr.sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          a.question.localeCompare(b.question),
      );
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      const ai = DEFAULT_CATEGORIES.indexOf(a);
      const bi = DEFAULT_CATEGORIES.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [faqs, query]);

  const openCreate = () => {
    const nextOrder =
      faqs.length > 0 ? Math.max(...faqs.map((f) => f.sort_order ?? 0)) + 1 : 0;
    setForm({ ...EMPTY_FORM, sort_order: nextOrder });
    setEditingId(null);
    setShowForm(true);
    setError("");
  };

  const openEdit = (faq) => {
    setForm({
      question: faq.question || "",
      answer: faq.answer || "",
      category: faq.category || DEFAULT_CATEGORIES[0],
      sort_order: faq.sort_order ?? 0,
      is_published: faq.is_published ?? true,
    });
    setEditingId(faq.id);
    setShowForm(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim() || !form.category.trim()) {
      setError("Question, answer, and category are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        question: form.question.trim(),
        answer: form.answer.trim(),
        category: form.category.trim(),
        sort_order: Number(form.sort_order) || 0,
        is_published: !!form.is_published,
      };
      if (editingId) {
        await api.put(`/admin/faqs/${editingId}`, payload, {
          headers: authHeaders(),
        });
        setSuccess("FAQ updated");
      } else {
        await api.post("/admin/faqs", payload, { headers: authHeaders() });
        setSuccess("FAQ created");
      }
      closeForm();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete this FAQ?\n\n"${faq.question}"`)) return;
    setError("");
    try {
      await api.delete(`/admin/faqs/${faq.id}`, { headers: authHeaders() });
      setSuccess("FAQ deleted");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to delete FAQ");
    }
  };

  const togglePublish = async (faq) => {
    setError("");
    try {
      await api.put(
        `/admin/faqs/${faq.id}`,
        { is_published: !faq.is_published },
        { headers: authHeaders() },
      );
      setSuccess(
        !faq.is_published ? "Published" : "Unpublished (hidden from site)",
      );
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update FAQ");
    }
  };

  const moveOrder = async (faq, dir) => {
    setError("");
    try {
      await api.put(
        `/admin/faqs/${faq.id}`,
        { sort_order: (faq.sort_order ?? 0) + (dir === "up" ? -1 : 1) },
        { headers: authHeaders() },
      );
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to reorder");
    }
  };

  return (
    <section data-testid="admin-faq-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-[#0E2A36]">FAQ Manager</h2>
          <p className="text-sm text-[#3D5560]">
            Add, edit, reorder, or hide questions shown on the public /faq page.
          </p>
        </div>
        <button
          data-testid="admin-faq-add"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-4 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <Plus className="w-4 h-4" /> Add FAQ
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3D5560]/60" />
        <input
          data-testid="admin-faq-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions..."
          className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
        />
      </div>

      {/* Messages */}
      {error && (
        <div
          data-testid="admin-faq-error"
          className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          data-testid="admin-faq-success"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm inline-flex items-center gap-2"
        >
          <CheckCircle2 className="w-4 h-4" /> {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div
          data-testid="admin-faq-form"
          className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_4px_20px_rgba(15,56,32,0.04)] p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold text-[#0E2A36]">
              {editingId ? "Edit FAQ" : "New FAQ"}
            </h3>
            <button
              data-testid="admin-faq-form-close"
              onClick={closeForm}
              className="w-8 h-8 rounded-full hover:bg-[#E1EEEA] flex items-center justify-center text-[#3D5560]"
              aria-label="Close form"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Question *
              </label>
              <input
                data-testid="admin-faq-input-question"
                type="text"
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
                placeholder="How do I sign up?"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Answer *
                <span className="ml-2 font-normal text-[#3D5560]/70">
                  (Supports **bold**, blank lines for paragraphs, and numbered lists)
                </span>
              </label>
              <textarea
                data-testid="admin-faq-input-answer"
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                rows={8}
                className="w-full px-4 py-3 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm font-mono leading-relaxed"
                placeholder="Write a helpful, clear answer..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Category *
              </label>
              <input
                data-testid="admin-faq-input-category"
                list="faq-categories"
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
                placeholder="e.g. Account & Getting Started"
              />
              <datalist id="faq-categories">
                {DEFAULT_CATEGORIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Sort order
              </label>
              <input
                data-testid="admin-faq-input-order"
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                id="faq-publish"
                data-testid="admin-faq-input-publish"
                type="checkbox"
                checked={form.is_published}
                onChange={(e) =>
                  setForm({ ...form, is_published: e.target.checked })
                }
                className="w-4 h-4 rounded border-[#C9DDD6] text-[#1F4E5F] focus:ring-[#1F4E5F]/40"
              />
              <label htmlFor="faq-publish" className="text-sm text-[#0E2A36]">
                Published (visible on /faq)
              </label>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <button
              data-testid="admin-faq-save"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] disabled:opacity-60 text-[#F4FAF8] px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editingId ? "Save changes" : "Create FAQ"}
            </button>
            <button
              onClick={closeForm}
              className="px-4 py-2.5 rounded-full text-sm font-semibold text-[#3D5560] hover:bg-[#E1EEEA]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#3D5560]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading FAQs…
        </div>
      ) : grouped.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#C9DDD6] bg-white px-6 py-12 text-center text-[#3D5560]">
          No FAQs yet. Click <span className="font-semibold">“Add FAQ”</span> to create one.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([category, items]) => (
            <div
              key={category}
              data-testid={`admin-faq-category-${category}`}
              className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_4px_20px_rgba(15,56,32,0.04)] overflow-hidden"
            >
              <div className="px-5 sm:px-6 py-4 border-b border-[#E1EEEA] bg-[#F4FAF8]/60 flex items-center justify-between">
                <h3 className="font-display font-bold text-[#1F4E5F]">{category}</h3>
                <span className="text-xs text-[#3D5560]/80">
                  {items.length} {items.length === 1 ? "question" : "questions"}
                </span>
              </div>
              <ul className="divide-y divide-[#E1EEEA]">
                {items.map((faq) => (
                  <li
                    key={faq.id}
                    data-testid={`admin-faq-row-${faq.id}`}
                    className="px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#3D5560]/70 bg-[#E1EEEA] px-2 py-0.5 rounded-full">
                          #{faq.sort_order ?? 0}
                        </span>
                        {!faq.is_published && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            Hidden
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 font-semibold text-[#0E2A36] text-[15px]">
                        {faq.question}
                      </p>
                      <p className="mt-1.5 text-sm text-[#3D5560] line-clamp-3 whitespace-pre-line">
                        {faq.answer}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap sm:flex-nowrap">
                      <IconBtn
                        title="Move up"
                        onClick={() => moveOrder(faq, "up")}
                        testId={`admin-faq-up-${faq.id}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </IconBtn>
                      <IconBtn
                        title="Move down"
                        onClick={() => moveOrder(faq, "down")}
                        testId={`admin-faq-down-${faq.id}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </IconBtn>
                      <IconBtn
                        title={faq.is_published ? "Unpublish" : "Publish"}
                        onClick={() => togglePublish(faq)}
                        testId={`admin-faq-toggle-${faq.id}`}
                      >
                        {faq.is_published ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </IconBtn>
                      <IconBtn
                        title="Edit"
                        onClick={() => openEdit(faq)}
                        testId={`admin-faq-edit-${faq.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </IconBtn>
                      <IconBtn
                        title="Delete"
                        onClick={() => handleDelete(faq)}
                        testId={`admin-faq-delete-${faq.id}`}
                        danger
                      >
                        <Trash2 className="w-4 h-4" />
                      </IconBtn>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function IconBtn({ children, onClick, title, testId, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testId}
      className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
        danger
          ? "text-rose-600 hover:bg-rose-50"
          : "text-[#3D5560] hover:bg-[#E1EEEA] hover:text-[#1F4E5F]"
      }`}
    >
      {children}
    </button>
  );
}
